/* eslint-disable sonarjs/cognitive-complexity */
'use client';
import { Path as IconPath, Pickup as IconPickup } from '@ahamove/icons/map';
import type { SpecialRequest } from '@ahm/api-wrappers-core';
import { validatePhoneNumberForLogin } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import { FloatingLabelInput } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Box,
  Button,
  Card,
  Divider,
  Group,
  rem,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useTranslations } from 'next-intl';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useLocaleSuffix } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

interface ModalSendSmsToRecipientProps {
  request: SpecialRequest;
  onCloseModal: () => void;
}

export default function SendSmsToRecipient({
  request,
  onCloseModal,
}: ModalSendSmsToRecipientProps) {
  const isMobile = useIsMobile();
  const localeSuffix = useLocaleSuffix();
  const tActions = useTranslations('common.actions');
  const tBooking = useTranslations('booking');
  const tAuth = useTranslations('system.auth');

  const paths = useLocationStore.use.paths();
  const { selectedService, setServiceRequest, serviceRequests } =
    useBookingStore((state) => state);

  const updatePathByIndex = useLocationStore.use.updatePathByIndex();
  const schema = z.object({
    name: z
      .string({
        required_error: 'Vui lòng nhập tên',
        invalid_type_error: 'Vui lòng nhập tên',
      })
      .min(2, { message: 'Vui lòng nhập ít nhất 2 ký tự' })
      .max(20, { message: 'Vui lòng nhập không quá 20 ký tự' }),
    mobile: z
      .string({
        required_error: 'Vui lòng nhập sdt',
        invalid_type_error: 'Vui lòng nhập sdt',
      })
      .refine((value) => validatePhoneNumberForLogin(value), {
        message: tAuth('invalidPhoneNumber'),
      })
      .or(z.literal(''))
      .refine((value) => value !== '', {
        message: 'Vui lòng nhập sdt',
      }),
  });

  type FormData = z.infer<typeof schema>;

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors, isValid },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: paths[1]?.name ?? '',
      mobile: paths[1]?.mobile ?? '',
    },
  });

  const onSubmit = (data: FormData) => {
    if (!selectedService?._id) return;

    updatePathByIndex(
      {
        ...paths[1],
        name: data.name,
        mobile: data.mobile,
      },
      1
    );
    const param = { ...serviceRequests };
    param[request._id as keyof typeof serviceRequests] = true;
    setServiceRequest(param);
    onCloseModal();
  };

  return (
    <Box p={0}>
      <Text py={rem(16)} c={'neutral.7'} size="sm">
        {request[`description_${localeSuffix}`]}
      </Text>

      <Divider mx={-24} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={isMobile ? rem(16) : rem(8)} py="md">
          <Title pb={rem(8)} order={5}>
            Thông tin của bạn
          </Title>

          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <Group gap={rem(8)}>
                  <IconPickup width={16} height={16} />
                  <Title order={6}>{tBooking('location.sender_details')}</Title>
                </Group>
                <FloatingLabelInput
                  label={tBooking('location.sender_name')}
                  h="auto"
                  value={value}
                  onChange={(e) => onChange(e)}
                  error={(errors.name?.message ?? '') as string}
                />
              </>
            )}
          />
          <Controller
            name="mobile"
            control={control}
            render={({ field: { onChange, value } }) => (
              <>
                <Group gap={rem(8)}>
                  <IconPath width={16} height={16} />
                  <Title order={6}>{tBooking('location.sender_details')}</Title>
                </Group>

                <FloatingLabelInput
                  label={tBooking('location.phone_number')}
                  h={'auto'}
                  value={value}
                  onChange={(e) => onChange(e)}
                  error={(errors.mobile?.message ?? '') as string}
                />
              </>
            )}
          />
        </Stack>

        <Divider mx={-24} />
        <Box py={rem(16)}>
          <Title order={5}>Nội dung tin nhắn</Title>
          <Card
            mt={rem(8)}
            style={{
              borderColor: 'var(--mantine-color-primary-6)',
            }}
            withBorder
            radius={8}
            bg={'primary.0'}
            p={rem(12)}
          >
            <Text size="sm" c="gray.6">
              {tBooking.rich('special_requests.template_sms_to_recipient', {
                span: (chunks) => (
                  <Text size="sm" component="span" fw={700} c="primary.6">
                    {chunks}
                  </Text>
                ),
                p: (chunks) => (
                  <Text size="sm" component="span" fw={700} c="gray.7">
                    {chunks}
                  </Text>
                ),
                name: watch('name'),
              })}
            </Text>
          </Card>
        </Box>

        <Button
          fullWidth
          size="md"
          mih={rem(48)}
          type="submit"
          disabled={!isValid}
        >
          {tActions('confirm')}
        </Button>
      </form>
    </Box>
  );
}
